import * as fileUtils from "../../../fileUtils";
import FilesFileUpload from "../../FilesFileUpload";
import DownloadButton from "../../common/DownloadButton";
import ClaimsDialog from "../ingestion/ClaimsDialog";
import SicCodesDialog from "../ingestion/SicCodes/SicCodesDialog";
import FileListTree from "./FileListTree";
import GetAppIcon from "@mui/icons-material/GetApp";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import EmailReceiver from "components/inputs/attachments/EmailReceiver";
import * as labels from "config.labels";
import { useCallback, useRef, useState } from "react";
import { connect } from "react-redux";
import * as actions from "store/actions/input/attachments/attachmentsActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as attachTempSelectors from "store/selectors/temp/attachments/tempAttachmentsSelectors";

export const Attachments = ({
  fileUploaded,
  fileUploadedWithPath,
  createEmptyDirectoryWithPath,
  attachedFilesForDownload,
  existingFileNames,
}) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const processLossRunRef = useRef(null);
  const processSicCodesRef = useRef(null);

  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    fileName: "",
    fileId: null,
    relativePath: null,
    duplicateNames: [],
  });

  const checkForDuplicates = useCallback(
    (fileName) => {
      const normalizedNewName = fileName.toLowerCase();
      const duplicates = existingFileNames.filter(
        (existing) => existing.name.toLowerCase() === normalizedNewName
      );
      return duplicates;
    },
    [existingFileNames]
  );

  // Standard drop handler (for single files without path)
  const onDrop = useCallback(
    (fileId, file) => {
      const fileName = file?.name ?? "";
      const duplicates = checkForDuplicates(fileName);

      if (duplicates.length > 0) {
        setDuplicateDialog({
          open: true,
          fileName,
          fileId,
          relativePath: null,
          duplicateNames: duplicates.map((d) => d.name),
        });
      } else {
        fileUploaded(fileId);
      }
    },
    [checkForDuplicates, fileUploaded]
  );

  const onDropWithPath = useCallback(
    (fileId, file, relativePath) => {
      const fileName = file?.name ?? "";
      const duplicates = checkForDuplicates(fileName);

      if (duplicates.length > 0) {
        setDuplicateDialog({
          open: true,
          fileName,
          fileId,
          relativePath,
          duplicateNames: duplicates.map((d) => d.name),
        });
      } else {
        if (relativePath && relativePath.includes("/")) {
          fileUploadedWithPath(fileId, relativePath);
        } else {
          fileUploaded(fileId);
        }
      }
    },
    [checkForDuplicates, fileUploaded, fileUploadedWithPath]
  );

  const onCreateEmptyDirectory = useCallback(
    (relativePath) => {
      createEmptyDirectoryWithPath(relativePath);
    },
    [createEmptyDirectoryWithPath]
  );

  const handleDuplicateConfirm = () => {
    if (duplicateDialog.fileId) {
      if (
        duplicateDialog.relativePath &&
        duplicateDialog.relativePath.includes("/")
      ) {
        fileUploadedWithPath(
          duplicateDialog.fileId,
          duplicateDialog.relativePath
        );
      } else {
        fileUploaded(duplicateDialog.fileId);
      }
    }
    setDuplicateDialog({
      open: false,
      fileName: "",
      fileId: null,
      relativePath: null,
      duplicateNames: [],
    });
  };

  const handleDuplicateCancel = () => {
    setDuplicateDialog({
      open: false,
      fileName: "",
      fileId: null,
      relativePath: null,
      duplicateNames: [],
    });
  };

  return (
    <>
      <Dialog open={duplicateDialog.open} onClose={handleDuplicateCancel}>
        <DialogTitle>{"Duplicate File Detected"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`A file named "${duplicateDialog.fileName}" already exists in the attachments. Do you want to upload it anyway?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDuplicateCancel} sx={{ color: "#d32f2f" }}>
            {"Cancel"}
          </Button>
          <Button
            onClick={handleDuplicateConfirm}
            color={"primary"}
            variant={"contained"}
          >
            {"Upload Anyway"}
          </Button>
        </DialogActions>
      </Dialog>
      <ClaimsDialog
        startFileProcessingRef={processLossRunRef}
        noDropBox={true}
      />
      <SicCodesDialog
        startFileProcessingRef={processSicCodesRef}
        noDropBox={true}
      />
      <Disabled ifReadOnly>
        <FilesFileUpload
          onDrop={onDrop}
          onDropWithPath={onDropWithPath}
          onCreateEmptyDirectory={onCreateEmptyDirectory}
          oneAtATime={false}
          asAtDateNotRequired={true}
          dropMessage={"Drop files or folders here to upload"}
        />
      </Disabled>
      <Disabled ifReadOnly>
        <EmailReceiver />
      </Disabled>
      <Component
        title={translateLabel(labels.ATTACHMENTS_TITLE, {
          default: "Attachments",
        })}
      >
        <FileListTree
          processLossRunRef={processLossRunRef}
          processSicCodesRef={processSicCodesRef}
        />
        <DownloadAll attachedFilesForDownload={attachedFilesForDownload} />
      </Component>
    </>
  );
};

const DownloadAll = ({ attachedFilesForDownload }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const [isLoading, setIsLoading] = useState(0);
  class counter {
    constructor(num) {
      this.count = num;
      setIsLoading(num);
    }
    dec() {
      this.count -= 1;
      setIsLoading(this.count);
    }
  }
  const doDownload = async () => {
    const cnt = new counter(Object.keys(attachedFilesForDownload).length);

    attachedFilesForDownload.forEach((e) => {
      fileUtils
        .saveFile(e.id, e.name)
        .then(() => cnt.dec())
        .catch(() => cnt.dec());
    });
  };
  return (
    <div style={{ display: "flex", justifyContent: "end" }}>
      <DownloadButton
        sx={{ marginLeft: "auto" }}
        onClick={doDownload}
        isLoading={isLoading > 0}
      >
        <GetAppIcon />{" "}
        {translateLabel(labels.ATTACHMENTS_DOWNLOAD_ALL, {
          default: "Download All",
        })}
      </DownloadButton>
    </div>
  );
};

const mapDispatchToProps = {
  fileUploaded: actions.fileUploaded,
  fileUploadedWithPath: actions.fileUploadedWithPath,
  createEmptyDirectoryWithPath: actions.createEmptyDirectoryWithPath,
};

const mapStateToProps = (state) => {
  const attachedFiles = attachTempSelectors.attachedFilesFlattened(state);
  return {
    attachedFilesForDownload: attachedFiles,
    existingFileNames: attachedFiles,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Attachments);
